import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Shuffle } from "lucide-react";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { demoFor, type DemoLine } from "./demo";
import {
  BRANE_PINNED,
  branePose,
  braneScene,
  braneScrub,
  flat,
  knotPose,
  knotScene,
  knotScrub,
  theirsPose,
  theirsScene,
  theirsScrub,
  threadChance,
} from "@/components/sheet/figures";
import { nouns } from "@/components/sheet/nouns";
import { PaperFigure } from "@/components/sheet/PaperFigure";
import { useArrived } from "@/components/sheet/useArrived";
import { BraneFlow, LoopFlow } from "./Flow";

/*
  The three rooms side by side, then one room each, each on its own colour of
  paper: String vermilion, Knot saffron, Loop cobalt. The comparison is the one
  place all three are defined together; the sections show one room each rather
  than redefining it (PRODUCT.md, Page Structure).

  In the comparison each room noun arrives cut into loose letters and is pulled
  together as it comes on screen. In the sections, the page's one thread
  (components/sheet/Thread.tsx) runs through each figure and the figure is
  what it meets there: in the String, theirs, which reaches in and collides
  with it; in the Knot, the other strands, woven through it a strip at a time;
  in the Loop, nothing but the thread itself, wound round once and round again.
  Every action on a figure is a real button.

  After the String, its other way in: the Brane. Not a room, so it is not in
  the comparison and has no field of its own colour: it lies on the bare
  sheet. The figure is a disc, you, with strips reaching it from every side,
  one after another as it is read: it is what you share, so that anyone can
  reach you. The page's thread is one of them, and goes on through it. Its example
  is the process (Flow.tsx): you make the Brane and copy its link, someone
  opens it and writes, and the String opens. The Loop's is the same idea from
  its creation: the form filled in, the room it opens with other people
  writing in it, and the room archived once the talk dies down. The wide Loops
  demo that sat under the Loop is gone (owner's decision, 2026-10-13): the
  phone under the hero already plays the feed, and a third telling of the same
  room only made the section long.
*/

/** How far theirs must be pulled off the thread, in box units, to come away and let chance deal another. */
const DETACH = 40;

const ROOMS = ["string", "knot", "loop"] as const;
type Room = (typeof ROOMS)[number];
const ROWS = ["access", "who", "identity"] as const;

/** A room noun as cut-out letters. Read as one word; set loose until `set`. */
function CutWord({ word }: { word: string }) {
  return (
    <em className="sh-cutword">
      {Array.from(word).map((ch, i) => (
        <span key={i} className="sh-cutword__ch">
          {ch}
        </span>
      ))}
    </em>
  );
}

export function Rooms() {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement | null>(null);
  const set = useArrived(ref, 0.2);
  return (
    <section ref={ref} id="rooms" className={cn("sh-rooms", set && "is-set")} aria-labelledby="sh-rooms-title">
      <div className="sh-wrap sh-rooms__head">
        <h2 id="sh-rooms-title" className="sh-h2">
          {t("rooms.compare.title")}
        </h2>
        <p className="sh-rooms__intro">{t("landing.rooms.intro")}</p>
      </div>
      <div className="sh-compare">
        {ROOMS.map((room) => (
          <article key={room} className={`sh-compare__col sh-field--${room}`}>
            <h3 className="sh-compare__name">
              <a href={`#${room}s`}>
                <CutWord word={t(`rooms.${room}.name`)} />
              </a>
            </h3>
            <p className="sh-compare__gloss">{t(`rooms.compare.gloss.${room}`)}</p>
            <dl className="sh-rows">
              {ROWS.map((row) => (
                <div key={row} className="sh-rows__row">
                  <dt>{t(`rooms.compare.row.${row}`)}</dt>
                  <dd>{nouns(t(`rooms.compare.${room}.${row}`))}</dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
}

function RoomSection({
  room,
  field = room === "brane" ? null : room,
  title,
  body,
  figure,
  children,
}: {
  room: Room | "brane";
  /** The colour of paper it lies on; null for the bare sheet, where the Brane lies. */
  field?: Room | null;
  title: string;
  body: string;
  figure: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section
      id={`${room}s`}
      className={cn("sh-room", `sh-room--${room}`, field && `sh-field--${field}`)}
      aria-labelledby={`sh-${room}-title`}
    >
      <div className="sh-wrap sh-room__grid">
        <div className="sh-room__text">
          <h2 id={`sh-${room}-title`} className="sh-h2 sh-room__title">
            {title}
          </h2>
          <p className="sh-body">{nouns(body)}</p>
        </div>
        <div className="sh-room__figure">{figure}</div>
        {children && <div className="sh-room__evidence">{children}</div>}
      </div>
    </section>
  );
}

function Lines({ lines }: { lines: DemoLine[] }) {
  return (
    <ol className="sh-lines">
      {lines.map((line, i) => {
        const showWho = !line.own && lines[i - 1]?.who !== line.who;
        return (
          <li key={i} className={cn("sh-line", line.own && "is-own")}>
            {showWho && <span className="sh-line__who">{line.who}</span>}
            <span className="sh-line__text">{line.text}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function StringSection() {
  const { t, i18n } = useTranslation();
  const demo = demoFor(i18n.language).string;
  const [chance, setChance] = useState({ angle: Math.PI * 1.1, bend: 1 });
  const pose = useMemo(() => theirsPose(chance.angle, chance.bend), [chance]);
  const start = useMemo(() => flat(theirsScene, theirsPose()), []);
  const scrub = useMemo(() => theirsScrub(pose), [pose]);
  return (
    <RoomSection
      room="string"
      title={t("landing.string.title")}
      body={t("landing.string.body")}
      figure={
        <>
          <PaperFigure
            scene={theirsScene}
            start={start}
            pose={pose}
            scrub={scrub}
            pull
            onRelease={(by) => {
              // Pulled hard enough, theirs comes away, and chance deals another.
              if (Math.hypot(by[0], by[1]) > DETACH) setChance(threadChance());
            }}
            label={t("landing.sheet.string.figure")}
          />
          <p className="sh-room__caption">
            <span>{t("landing.sheet.string.caption")}</span>
            <button type="button" className="sh-tab" onClick={() => setChance(threadChance())}>
              <Icon glyph={Shuffle} scale="inline" />
              {t("landing.sheet.string.again")}
            </button>
          </p>
        </>
      }
    >
      <article className="sh-panel" aria-label={t("landing.string.panel")}>
        <header className="sh-panel__head">
          <h3 className="sh-panel__name">{demo.them}</h3>
          <p className="sh-panel__meta">
            <span>
              <em>{t("rooms.string.name")}</em>
            </span>
            <span>{t("y.newString.matchedOnOne")}</span>
            <span className="sh-panel__mood">{t("y.newString.moods.music")}</span>
          </p>
        </header>
        <Lines lines={demo.lines} />
        <footer className="sh-panel__foot">{t("rooms.compare.string.identity")}</footer>
      </article>
    </RoomSection>
  );
}

export function BraneSection() {
  const { t } = useTranslation();
  const pose = useMemo(() => branePose(), []);
  const start = useMemo(() => flat(braneScene, pose), [pose]);
  const scrub = useMemo(() => braneScrub(pose), [pose]);
  return (
    <RoomSection
      room="brane"
      title={t("landing.brane.title")}
      body={t("landing.brane.body")}
      figure={
        <>
          <PaperFigure
            scene={braneScene}
            start={start}
            pose={pose}
            scrub={scrub}
            pull
            pinned={BRANE_PINNED}
            label={t("landing.sheet.brane.figure")}
          />
          <p className="sh-room__caption">
            <span>{nouns(t("landing.sheet.brane.caption"))}</span>
          </p>
        </>
      }
    >
      <BraneFlow />
    </RoomSection>
  );
}

export function KnotSection() {
  const { t, i18n } = useTranslation();
  const demo = demoFor(i18n.language).knot;
  const pose = useMemo(() => knotPose(), []);
  const start = useMemo(() => flat(knotScene, pose), [pose]);
  const scrub = useMemo(() => knotScrub(pose), [pose]);
  return (
    <RoomSection
      room="knot"
      title={t("landing.knot.title")}
      body={t("landing.knot.body")}
      figure={
        <>
          <PaperFigure
            scene={knotScene}
            start={start}
            pose={pose}
            scrub={scrub}
            omit={[1]}
            label={t("landing.sheet.knot.figure")}
          />
          <p className="sh-room__caption">
            <span>{t("landing.sheet.knot.caption")}</span>
          </p>
        </>
      }
    >
      <article className="sh-panel" aria-label={t("landing.knot.panel")}>
        <header className="sh-panel__head">
          <h3 className="sh-panel__name">{demo.name}</h3>
          <p className="sh-panel__meta">
            <span>
              <em>{t("rooms.knot.name")}</em>
            </span>
            <span>{demo.meta}</span>
          </p>
        </header>
        <Lines lines={demo.lines} />
        <footer className="sh-panel__foot">
          <span className="sh-panel__action">{t("landing.knot.request")}</span>
        </footer>
      </article>
    </RoomSection>
  );
}

export function LoopSection() {
  const { t } = useTranslation();
  return (
    <RoomSection
      room="loop"
      title={t("landing.loop.title")}
      body={t("landing.loop.body")}
      figure={
        <>
          {/* The thread winds here; the figure is the place it winds, and what it shows for a screen reader. */}
          <div className="sh-figure" role="img" aria-label={t("landing.sheet.loop.figure")} />
          <p className="sh-room__caption">
            <span>{t("landing.sheet.loop.caption")}</span>
          </p>
        </>
      }
    >
      <LoopFlow />
    </RoomSection>
  );
}
